/*
 * Draft 7 screens: Loose ends (a Home card), the Hiring board (HR), and Schedule tools: capacity for the next two weeks,
 * texting a day, and time off. Routes, their order, Best order and route day moves live in the Schedule's Route view
 * (RouteDay.tsx) since 2026-10-29.
 */
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  APPLICANT_STAGE_WORDS,
  APPLICANT_STAGES,
  addDays,
  type ApplicantRow,
  type ApplicantStage,
  type DayTextAnswer,
} from "@steward/shared";
import {
  useAddTimeOff,
  useApplicants,
  useCapacity,
  useClearTimeOff,
  useDayText,
  useHire,
  useMoveApplicant,
  usePeople,
  useTimeOff,
} from "../api/hooks";
import { useSession } from "../app/session";
import { Choice, Combobox, InputText } from "../components/fields";
import { shortDate, todayIn } from "../components/format";
import { Modal, useToast } from "../components/overlays";
import { Button, Card, Details, EmptyState, Page, Skeleton, StatusLabel, ago } from "../components/parts";
import { errText } from "../components/work";

// Hiring --------------------------------------------------------------------------------------------------------------

// The board's columns. Archived ones leave the board and sit in a list under it (owner, 2026-11-01).
const BOARD = APPLICANT_STAGES.filter((s) => s !== "archived");

export function Hiring() {
  const list = useApplicants();
  const [openId, setOpenId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  if (!list.data) return <Skeleton rows={6} />;
  const all = list.data.applicants;
  const by = (s: ApplicantStage) => all.filter((a) => a.stage === s);
  const archived = by("archived");
  const open = all.find((a) => a.id === openId) ?? null;
  return (
    <Page title="Hiring">
      {all.length === 0 ? (
        <EmptyState icon="team" title="No applicants yet">
          People who apply on the website careers page show here.
        </EmptyState>
      ) : (
        <div className="stack">
          <div className="pipeline hiring">
            {BOARD.map((s) => (
              <section key={s} className="stage" aria-label={APPLICANT_STAGE_WORDS[s]}>
                <div className="stage-head">
                  <b>{APPLICANT_STAGE_WORDS[s]}</b> <span className="muted num">{by(s).length}</span>
                </div>
                <div className="stage-list">
                  {by(s).length === 0 ? <p className="muted stage-empty">No one here</p> : null}
                  {by(s).map((a) => (
                    <ApplicantCard key={a.id} a={a} onOpen={() => setOpenId(a.id)} />
                  ))}
                </div>
              </section>
            ))}
          </div>
          {archived.length ? (
            <div className="stack">
              <div>
                <Button type="tertiary" size="small" onClick={() => setShowArchived(!showArchived)}>
                  {showArchived ? "Hide archived" : `Show archived (${String(archived.length)})`}
                </Button>
              </div>
              {showArchived ? (
                <div className="hiring-archived">
                  {archived.map((a) => (
                    <ApplicantCard key={a.id} a={a} onOpen={() => setOpenId(a.id)} />
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
      {open ? <ApplicantSheet a={open} onClose={() => setOpenId(null)} /> : null}
    </Page>
  );
}

/** One applicant on the board: the whole card opens them. */
function ApplicantCard({ a, onOpen }: { a: ApplicantRow; onOpen: () => void }) {
  return (
    <button type="button" className="lead applicant" onClick={onOpen}>
      <b>{a.name}</b>
      <span className="muted">{[a.position, a.zip, ago(a.createdAt)].filter(Boolean).join(" · ")}</span>
      {a.about ? <p className="clamp">{a.about}</p> : null}
    </button>
  );
}

/** An applicant opened: how to reach them, what they wrote, and moving, hiring or archiving them. */
function ApplicantSheet({ a, onClose }: { a: ApplicantRow; onClose: () => void }) {
  const move = useMoveApplicant();
  const hire = useHire();
  const toast = useToast();
  const { can } = useSession();
  const edit = can("people.update") && a.stage !== "hired";
  const setStage = (stage: ApplicantStage, said: string) =>
    move.mutate(
      { id: a.id, stage },
      {
        onSuccess: () => {
          toast(said);
          if (stage === "archived") onClose();
        },
        onError: (e) => toast(errText(e), { kind: "error" }),
      },
    );
  const footer = edit ? (
    a.stage === "archived" ? (
      <Button loading={move.isPending} onClick={() => setStage("new", `${a.name} is back on the board`)}>
        Put back on the board
      </Button>
    ) : (
      <>
        <Button type="secondary" icon="trash" loading={move.isPending} onClick={() => setStage("archived", `${a.name} archived`)}>
          Archive
        </Button>
        {can("people.invite") ? (
          <Button
            loading={hire.isPending}
            onClick={() =>
              hire.mutate(
                { id: a.id, invite: true },
                {
                  onSuccess: () => {
                    toast(`${a.name} is on the team. The invite is on its way.`);
                    onClose();
                  },
                  onError: (e) => toast(errText(e), { kind: "error" }),
                },
              )
            }
          >
            Hire
          </Button>
        ) : null}
      </>
    )
  ) : null;
  return (
    <Modal title={a.name} open onClose={onClose} size="full" footer={footer}>
      <div className="stack">
        <Details
          rows={[
            ["Applied", `${shortDate(a.createdAt.slice(0, 10))}${a.source === "website" ? " on the website" : ""}`],
            ["For", a.position],
            ["Phone", a.phone ? <a href={`tel:${a.phone}`}>{a.phone}</a> : null],
            ["Email", a.email ? <a href={`mailto:${a.email}`}>{a.email}</a> : null],
            ["ZIP", a.zip],
          ]}
        />
        {a.about ? <p className="applicant-about">{a.about}</p> : null}
        {edit && a.stage !== "archived" ? (
          <Choice
            label="Stage"
            value={a.stage}
            onChange={(s) => setStage(s, `${a.name} moved to ${APPLICANT_STAGE_WORDS[s]}`)}
            options={BOARD.filter((s) => s !== "hired").map((s) => ({ value: s, label: APPLICANT_STAGE_WORDS[s] }))}
          />
        ) : (
          <p className="muted">{APPLICANT_STAGE_WORDS[a.stage]}</p>
        )}
      </div>
    </Modal>
  );
}

// Schedule tools --------------------------------------------------------------------------------------------------------

export function ScheduleTools() {
  const { company, can } = useSession();
  const today = todayIn(company.timezone);
  const [date, setDate] = useState(addDays(today, 1));
  const edit = can("schedule.update");
  return (
    <Page title="Time off and capacity">
      <div className="stack">
        <CapacityCard from={today} />
        {edit ? (
          <Card title="Text everyone on a day">
            <InputText label="Day" type="date" value={date} onChange={(e) => setDate(e.target.value || date)} />
            <DayText date={date} />
          </Card>
        ) : null}
        <TimeOffCard from={today} edit={edit} />
      </div>
    </Page>
  );
}

function CapacityCard({ from }: { from: string }) {
  const cap = useCapacity(from, addDays(from, 13));
  if (!cap.data) return <Skeleton rows={2} />;
  if (!cap.data.on)
    return (
      <Card title="Capacity">
        <p className="muted">
          Capacity is off. Turn it on in{" "}
          <Link to="/schedule/settings" hash="capacity">
            Schedule settings
          </Link>{" "}
          to see how full each day is.
        </p>
      </Card>
    );
  return (
    <Card title="Capacity, next two weeks" flush>
      <ul className="auto-list">
        {cap.data.days.map((d) => (
          <li key={d.date} className="setting-row">
            <span className="what">
              <b>{shortDate(d.date)}</b>
              <span>
                {d.teams} {d.teams === 1 ? "team" : "teams"}, {d.people} people, {d.hoursBooked} of {d.hoursHeld} hours booked
              </span>
            </span>
            <StatusLabel status={d.teams === 0 ? "inactive" : d.open ? "success" : "critical"}>
              {d.teams === 0 ? "Closed" : d.open ? "Open" : "Full"}
            </StatusLabel>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function DayText({ date, routeId }: { date: string; routeId?: string }) {
  const send = useDayText();
  const toast = useToast();
  const [message, setMessage] = useState("Hi {first}, a note about your {service} on {day}, {date}. ");
  const [preview, setPreview] = useState<DayTextAnswer | null>(null);
  const body = { date, message, routeId: routeId ?? null, leaveOut: [], skipReminders: true };
  return (
    <div className="stack auto-step">
      <b>Text everyone that day</b>
      <label className="field wide">
        <span className="label">Message. Fill-ins: {"{first}, {service}, {day}, {date}, {address}"}</span>
        <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} />
      </label>
      {preview ? (
        <div className="stack">
          {preview.samples.map((s) => (
            <pre key={s.to} className="message-preview">
              {s.name}: {s.text}
            </pre>
          ))}
          {preview.skipped.length ? (
            <span className="muted">Skipped: {preview.skipped.map((s) => `${s.name} (${s.why})`).join(", ")}</span>
          ) : null}
        </div>
      ) : null}
      <div className="row">
        <Button
          type="secondary"
          loading={send.isPending}
          onClick={() => send.mutate({ ...body, dry: true }, { onSuccess: setPreview })}
        >
          See who gets it
        </Button>
        {preview ? (
          <Button
            loading={send.isPending}
            onClick={() =>
              send.mutate(
                { ...body, dry: false },
                {
                  onSuccess: (r) => {
                    setPreview(null);
                    toast(`Sent ${String(r.sent)}. Their reminders for that day are off.`);
                  },
                },
              )
            }
          >
            Send
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function TimeOffCard({ from, edit }: { from: string; edit: boolean }) {
  const { company } = useSession();
  const off = useTimeOff(from, addDays(from, 90));
  const team = usePeople(company.id);
  const add = useAddTimeOff();
  const clear = useClearTimeOff();
  const toast = useToast();
  const [draft, setDraft] = useState<{
    personId: string;
    fromDay: string;
    toDay: string;
    whole: "day" | "part";
    fromTime: string;
    toTime: string;
  } | null>(null);
  return (
    <Card
      title="Time off"
      flush
      action={
        edit && !draft ? (
          <Button
            type="secondary"
            size="small"
            icon="plus"
            onClick={() =>
              setDraft({ personId: "", fromDay: from, toDay: from, whole: "day", fromTime: "09:00", toTime: "12:00" })
            }
          >
            Add time off
          </Button>
        ) : null
      }
    >
      {draft ? (
        <div className="stack auto-step">
          <Combobox
            label="Who"
            value={draft.personId}
            onChange={(personId) => setDraft({ ...draft, personId })}
            options={(team.data ?? [])
              .filter((p) => p.active)
              .map((p) => ({ value: p.id, label: `${p.firstName} ${p.lastName}`.trim() }))}
          />
          <div className="row">
            <InputText
              label="First day"
              type="date"
              value={draft.fromDay}
              onChange={(e) => setDraft({ ...draft, fromDay: e.target.value })}
            />
            <InputText
              label="Last day"
              type="date"
              value={draft.toDay}
              onChange={(e) => setDraft({ ...draft, toDay: e.target.value })}
            />
          </div>
          <Choice
            label="How long"
            value={draft.whole}
            options={[
              { value: "day", label: "Whole day" },
              { value: "part", label: "Part of the day" },
            ]}
            onChange={(whole) => setDraft({ ...draft, whole })}
          />
          {draft.whole === "part" ? (
            <div className="row">
              <InputText
                label="From"
                type="time"
                value={draft.fromTime}
                onChange={(e) => setDraft({ ...draft, fromTime: e.target.value })}
              />
              <InputText
                label="To"
                type="time"
                value={draft.toTime}
                onChange={(e) => setDraft({ ...draft, toTime: e.target.value })}
              />
            </div>
          ) : null}
          <div className="row end">
            <Button type="tertiary" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button
              loading={add.isPending}
              onClick={() =>
                add.mutate(
                  {
                    personId: draft.personId,
                    fromDay: draft.fromDay,
                    toDay: draft.toDay,
                    ...(draft.whole === "part" ? { fromTime: draft.fromTime, toTime: draft.toTime } : {}),
                  },
                  { onSuccess: () => setDraft(null), onError: (e) => toast(errText(e), { kind: "error" }) },
                )
              }
            >
              Save
            </Button>
          </div>
        </div>
      ) : null}
      {!off.data ? (
        <Skeleton rows={2} />
      ) : off.data.timeOff.length === 0 ? (
        <p className="muted auto-intro">Nobody is off in the next 90 days.</p>
      ) : (
        <ul className="auto-list">
          {off.data.timeOff.map((t) => (
            <li key={t.id} className="setting-row">
              <span className="what">
                <b>{t.personName}</b>
                <span>
                  {shortDate(t.fromDay)}
                  {t.toDay !== t.fromDay ? ` to ${shortDate(t.toDay)}` : ""}
                  {t.fromTime ? `, ${t.fromTime} to ${t.toTime ?? ""}` : ", whole day"}
                  {t.reason ? `. ${t.reason}` : ""}
                </span>
              </span>
              {edit ? (
                <Button type="tertiary" size="small" onClick={() => clear.mutate(t.id)}>
                  Clear
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
